import React, { createContext, useContext, useState } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { X } from 'lucide-react';

interface ToastContextType {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<'success' | 'error'>('success');

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setMessage(message);
    setType(type);
    setOpen(true);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      <Toast.Provider swipeDirection="right">
        {children}
        <Toast.Root
          className={`
            fixed bottom-4 right-4 z-50
            rounded-lg shadow-lg
            ${type === 'success' ? 'bg-green-100' : 'bg-red-100'}
            p-4 data-[state=open]:animate-slideIn
            data-[state=closed]:animate-hide
            data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]
            data-[swipe=cancel]:translate-x-0
            data-[swipe=end]:animate-swipeOut
          `}
          open={open}
          onOpenChange={setOpen}
        >
          <div className="flex items-center justify-between">
            <Toast.Description className={`text-sm ${type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {message}
            </Toast.Description>
            <Toast.Close className="ml-4">
              <X className={`w-4 h-4 ${type === 'success' ? 'text-green-600' : 'text-red-600'}`} />
            </Toast.Close>
          </div>
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}